import { createLogger } from "../utils/logger.js";
import { eulerToQuaternion, getOdometryPositions, getZeroReference, recordGpsEvent, setZeroPos } from "gps-plus-slam-js";
//#region ../src/state/gps-event-coordinator.ts
const log = createLogger("RecordingCoordinator");
/** Counter for generating unique GPS point IDs */
let gpsEventCounter = 0;
/**
* State for device orientation (captured separately from GPS)
*/
let lastDeviceOrientation = null;
/**
* Update the cached device orientation.
* Called by orientation watch, used when GPS event arrives.
*/
function updateDeviceOrientation(orientation) {
	lastDeviceOrientation = orientation;
}
/**
* Get the current cached device orientation.
*/
function getLastDeviceOrientation() {
	return lastDeviceOrientation;
}
/**
* Clear cached state (for testing or session reset)
*/
function resetCoordinatorState() {
	lastDeviceOrientation = null;
	gpsEventCounter = 0;
}
/**
* Extract raw odometry position tuple from ARPose.
*
* Returns the raw WebXR position without coordinate conversion.
* The reducer applies the WebXR→NUE transform when storing into state
* (raw-storage pattern, see docs/2026-04-09-raw-storage-convert-on-read.md).
*
* WebXR local-floor frame: X=East, Y=Up, Z=South (toward viewer / backward).
*/
function extractOdomPosition(arPose) {
	return [
		arPose.position.x,
		arPose.position.y,
		arPose.position.z
	];
}
/**
* Extract odometry rotation tuple from ARPose.
*/
function extractOdomRotation(arPose) {
	return [
		arPose.orientation.x,
		arPose.orientation.y,
		arPose.orientation.z,
		arPose.orientation.w
	];
}
/**
* Build a RawGpsPoint from GPS position data.
* Returns only raw sensor fields — no derived fields (coordinates, weight,
* zeroRef, deviceRotation). Derived fields are computed by the library
* reducer when the action is dispatched (raw-storage pattern).
*
* @param gpsPosition - GPS position from Geolocation API
* @param deviceOrientation - Optional device orientation from sensors
* @returns RawGpsPoint ready for the action payload
*/
function buildRawGpsPoint(gpsPosition, deviceOrientation) {
	const compassAbsolute = deviceOrientation ? deviceOrientation.absolute : void 0;
	return {
		id: `gps-${++gpsEventCounter}`,
		latitude: gpsPosition.lat,
		longitude: gpsPosition.lon,
		altitude: gpsPosition.altitude ?? void 0,
		latLongAccuracy: gpsPosition.accuracy,
		altitudeAccuracy: gpsPosition.altitudeAccuracy ?? void 0,
		heading: gpsPosition.heading ?? void 0,
		speed: gpsPosition.speed ?? void 0,
		compassAbsolute,
		timestamp: gpsPosition.timestamp
	};
}
/**
* Build a RecordGpsEventPayload from GPS position and AR pose.
* This is a pure function for testability.
*
* Stores only raw sensor data in the payload. The library reducer
* computes derived fields (coordinates, weight, deviceRotation)
* when the action is processed (raw-storage pattern).
*
* @param gpsPosition - GPS position from Geolocation API
* @param arPose - AR pose from WebXR
* @param deviceOrientation - Optional device orientation from sensors
* @returns RecordGpsEventPayload ready for dispatch
*/
function buildRecordGpsEventPayload(gpsPosition, arPose, deviceOrientation) {
	const rawDeviceOrientation = deviceOrientation?.alpha != null && deviceOrientation?.beta != null && deviceOrientation?.gamma != null ? {
		alpha: deviceOrientation.alpha,
		beta: deviceOrientation.beta,
		gamma: deviceOrientation.gamma,
		absolute: deviceOrientation.absolute
	} : void 0;
	return {
		odomPosition: extractOdomPosition(arPose),
		odomRotation: extractOdomRotation(arPose),
		rawGpsPoint: buildRawGpsPoint(gpsPosition, deviceOrientation),
		rawDeviceOrientation
	};
}
/**
* Create a GPS position handler that dispatches combined GPS+AR events.
*
* This is the CRITICAL function that implements the correct architecture:
* - Called when GPS arrives
* - Reads current AR pose
* - Dispatches single combined action
*
* @param config - Configuration with store and getArPose function
* @returns Callback function for GPS watch
*/
function createGpsPositionHandler(config) {
	const { store, getArPose } = config;
	return (position) => {
		if (!store.getState().recording.isRecording) return;
		const arPose = getArPose();
		if (!arPose) {
			log.warn("GPS arrived but no AR pose available");
			return;
		}
		if (!store.getState().gpsData) {
			const zeroRef = {
				lat: position.lat,
				lon: position.lon
			};
			store.dispatch(setZeroPos(zeroRef));
			log.info(`Set zero reference: ${zeroRef.lat}, ${zeroRef.lon}`);
		}
		if (!getZeroReference(store.getState())) {
			log.error("Failed to get zero reference");
			return;
		}
		const payload = buildRecordGpsEventPayload(position, arPose, lastDeviceOrientation);
		store.dispatch(recordGpsEvent(payload));
		const eventCount = getOdometryPositions(store.getState()).length;
		log.info(`Recorded GPS event #${eventCount}`);
	};
}
//#endregion
export { buildRawGpsPoint, buildRecordGpsEventPayload, createGpsPositionHandler, eulerToQuaternion, extractOdomPosition, extractOdomRotation, getLastDeviceOrientation, resetCoordinatorState, updateDeviceOrientation };
